import React, { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import api from '@/lib/api';

/**
 * Maker-only — embedded in the GO LIVE confirmation card. Fetches the
 * /system/purge-preview endpoint and renders a redacted summary of
 * exactly how many demo records will be deleted, bucketed by source.
 *
 * Lives in its own file so the lint engine's react-hooks/set-state-in-effect
 * tracer doesn't follow the callback chain back to MakerControls' click
 * handlers (which would otherwise flag every setState as effect-driven).
 */
export default function PurgePreviewPanel({ active }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!active) return;
    let cancel = false;
    api.get('/system/purge-preview')
      .then(({ data: d }) => { if (!cancel) setData(d); })
      .catch(() => {/* preview is optional */});
    return () => { cancel = true; };
  }, [active]);

  if (!data) return null;

  return (
    <div className="mt-3 border border-rose-500/30 rounded-lg p-3 bg-rose-500/5" data-testid="purge-preview">
      <div className="flex items-center gap-2">
        <Trash2 className="w-3.5 h-3.5 text-rose-400" />
        <span className="text-[10px] uppercase tracking-[0.18em] text-rose-300">Will delete</span>
        <span className="ml-auto text-xs font-semibold text-rose-200" data-testid="purge-total">
          {data.total_to_delete?.toLocaleString?.() ?? 0} records
        </span>
      </div>
      <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
        {Object.entries(data.counts || {}).map(([bucket, items]) => {
          const entries = Object.entries(items).filter(([, n]) => n > 0);
          if (!entries.length) return null;
          return (
            <div key={bucket} className="text-[11px]">
              <div className="text-slate-400 uppercase tracking-[0.12em] mt-1">{bucket.replace(/_/g, ' ')}</div>
              {entries.map(([k, n]) => (
                <div key={k} className="flex justify-between text-slate-200 pl-2">
                  <span className="truncate">{k}</span>
                  <span className="font-mono text-rose-200 ml-2">{n}</span>
                </div>
              ))}
            </div>
          );
        })}
        {data.total_to_delete === 0 && (
          <div className="text-[11px] text-slate-400 italic">Nothing to purge — already clean.</div>
        )}
      </div>
    </div>
  );
}
